import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import map from 'lodash/map';
import startCase from 'lodash/startCase';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  Area,
  Line,
  ComposedChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import WebAPIClient from '../../../../api';
import { DEMAND_APP_ID } from '../../../../store/demand';
import {
  StyledSubtabs,
  StyledSubtab,
} from '../../../../components/StyledSubtabs';
import CoincidencePeaks from '../CoincidencePeaks';
import WeatherChartContainer from '../PJMMarkets/WeatherChart';
import ModelingChartContainer from '../PJMMarkets/ModelingChart';

// ─── helpers ──────────────────────────────────────────────────────────────────

const TIER_CONFIG = {
  imminent: { label: 'Imminent', color: 'error' },
  likely: { label: 'Likely', color: 'warning' },
  elevated: { label: 'Elevated', color: 'warning' },
  monitoring: { label: 'Monitoring', color: 'info' },
};

function fmtHour(h) {
  if (h == null) return '—';
  const d = dayjs().startOf('day').add(h, 'hour');
  return d.format('h A');
}

function fmtLmp(v) {
  if (v == null || v === 0) return '—';
  return `$${v.toFixed(2)}/MWh`;
}

function fmtMw(v) {
  if (v == null) return '—';
  return `${(v / 1000).toFixed(1)} GW`;
}

// ─── ProbabilityBar ───────────────────────────────────────────────────────────

function ProbabilityBar({ label, value }) {
  const pct = Math.round(value ?? 0);
  const color =
    pct >= 90
      ? 'error'
      : pct >= 70
        ? 'warning'
        : pct >= 40
          ? 'info'
          : 'success';
  return (
    <Box>
      <Stack direction='row' justifyContent='space-between' sx={{ mb: 0.25 }}>
        <Typography variant='caption' color='text.secondary'>
          {label}
        </Typography>
        <Typography variant='caption' fontWeight={600}>
          {pct}%
        </Typography>
      </Stack>
      <LinearProgress
        variant='determinate'
        value={pct}
        color={color}
        sx={{ height: 5, borderRadius: 3 }}
      />
    </Box>
  );
}

// ─── TodayChart ───────────────────────────────────────────────────────────────

function TodayChart({ intervals }) {
  if (!intervals?.length) return null;
  const data = intervals.map((d) => ({
    hour: dayjs.unix(d.timestamp).format('h A'),
    demand: d.demand_mw != null ? Math.round(d.demand_mw) : null,
    rtm: d.rtm_lmp,
    dam: d.dam_lmp,
  }));

  return (
    <ResponsiveContainer width='100%' height={140}>
      <ComposedChart
        data={data}
        margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
      >
        <XAxis
          dataKey='hour'
          tick={{ fontSize: 10 }}
          tickLine={false}
          axisLine={false}
          interval='preserveStartEnd'
        />
        <YAxis
          yAxisId='mw'
          tick={{ fontSize: 10 }}
          tickLine={false}
          axisLine={false}
          width={44}
          tickFormatter={(v) => `${(v / 1000).toFixed(0)}GW`}
        />
        <YAxis
          yAxisId='lmp'
          orientation='right'
          tick={{ fontSize: 10 }}
          tickLine={false}
          axisLine={false}
          width={36}
          tickFormatter={(v) => `$${v}`}
        />
        <RechartsTooltip
          contentStyle={{ fontSize: 11 }}
          formatter={(v, name) =>
            name === 'demand'
              ? [`${v} MW`, 'Demand']
              : name === 'rtm'
                ? [`$${v?.toFixed(2)}`, 'RTM LMP']
                : [`$${v?.toFixed(2)}`, 'DAM LMP']
          }
        />
        <Area
          yAxisId='mw'
          type='monotone'
          dataKey='demand'
          stroke='#1976d2'
          fill='#1976d2'
          fillOpacity={0.15}
          strokeWidth={1.5}
          dot={false}
          connectNulls
        />
        <Line
          yAxisId='lmp'
          type='monotone'
          dataKey='rtm'
          stroke='#ed6c02'
          strokeWidth={1.5}
          dot={false}
          connectNulls
        />
        <Line
          yAxisId='lmp'
          type='monotone'
          dataKey='dam'
          stroke='#ed6c02'
          strokeWidth={1}
          strokeDasharray='4 3'
          dot={false}
          connectNulls
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ─── GridIntelligence ─────────────────────────────────────────────────────────

function GridIntelligence({ rto }) {
  const [status, setStatus] = useState(null);
  const [intervals, setIntervals] = useState([]);
  const [annualPeaks, setAnnualPeaks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!rto?.rto_id) return;
    const client = new WebAPIClient(DEMAND_APP_ID);
    setLoading(true);
    Promise.all([
      client.GET(`/resource/grid/status`),
      client.GET(`/resource/grid/today-intervals`),
      client.GET(`/resource/grid/annual-peaks`),
    ])
      .then(([s, i, p]) => {
        setStatus(s);
        setIntervals(i ?? []);
        setAnnualPeaks(p ?? []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [rto]);

  if (loading) {
    return (
      <Stack direction='row' alignItems='center' spacing={1} sx={{ py: 2 }}>
        <CircularProgress size={16} />
        <Typography variant='caption' color='text.secondary'>
          Loading grid data…
        </Typography>
      </Stack>
    );
  }

  const tierCfg = status?.alert_tier ? TIER_CONFIG[status.alert_tier] : null;

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1.5}
      alignItems='stretch'
    >
      {/* Status + prices */}
      <Card variant='outlined' sx={{ flex: 1, minWidth: 220 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            sx={{ mb: 1 }}
          >
            <Typography variant='subtitle2' fontWeight={700}>
              {rto.rto_id}
            </Typography>
            <Stack direction='row' spacing={0.5}>
              {rto.shed && (
                <Chip size='small' label='Shed Active' color='error' />
              )}
              {tierCfg && (
                <Chip
                  size='small'
                  label={tierCfg.label}
                  color={tierCfg.color}
                />
              )}
              {!rto.shed && !tierCfg && (
                <Chip
                  size='small'
                  label='Normal'
                  color='success'
                  variant='outlined'
                />
              )}
            </Stack>
          </Stack>

          <Typography
            variant='caption'
            color='text.secondary'
            display='block'
            sx={{ mb: 1.5 }}
          >
            {status?.description ?? ''}
          </Typography>

          <Divider sx={{ mb: 1.5 }} />

          <Stack spacing={0.75}>
            <Stack direction='row' justifyContent='space-between'>
              <Typography variant='caption' color='text.secondary'>
                Hub RTM LMP
              </Typography>
              <Typography variant='caption' fontWeight={600}>
                {fmtLmp(status?.hub_rtm)}
              </Typography>
            </Stack>
            <Stack direction='row' justifyContent='space-between'>
              <Typography variant='caption' color='text.secondary'>
                Hub DAM LMP
              </Typography>
              <Typography variant='caption' fontWeight={600}>
                {fmtLmp(status?.hub_dam)}
              </Typography>
            </Stack>
            <Stack direction='row' justifyContent='space-between'>
              <Typography variant='caption' color='text.secondary'>
                Reserve Margin
              </Typography>
              <Typography variant='caption' fontWeight={600}>
                {status?.reserve_margin_pct != null
                  ? `${status.reserve_margin_pct}%`
                  : '—'}
              </Typography>
            </Stack>
            <Stack direction='row' justifyContent='space-between'>
              <Typography variant='caption' color='text.secondary'>
                Peak Threshold
              </Typography>
              <Typography variant='caption' fontWeight={600}>
                {rto.peak_percent != null ? `${rto.peak_percent}%` : '—'}
              </Typography>
            </Stack>
          </Stack>

          {status?.updated_at && (
            <Typography
              variant='caption'
              color='text.disabled'
              display='block'
              sx={{ mt: 1.5 }}
            >
              Updated {dayjs(status.updated_at).format('h:mm A')}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Prediction model */}
      <Card variant='outlined' sx={{ flex: 1, minWidth: 220 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant='subtitle2' fontWeight={700} sx={{ mb: 1.5 }}>
            CP Prediction Model
          </Typography>

          {status?.probability != null ? (
            <Stack spacing={1.25}>
              <ProbabilityBar
                label='Peak Probability'
                value={status.probability}
              />
              {status.signals && (
                <>
                  <ProbabilityBar
                    label='Load'
                    value={status.signals.load_percentile}
                  />
                  <ProbabilityBar
                    label='Enthalpy'
                    value={status.signals.enthalpy}
                  />
                  <ProbabilityBar
                    label='Trajectory'
                    value={status.signals.trajectory}
                  />
                  <ProbabilityBar label='LMP' value={status.signals.lmp} />
                </>
              )}
              <Divider />
              <Stack direction='row' justifyContent='space-between'>
                <Typography variant='caption' color='text.secondary'>
                  Predicted Peak Hour
                </Typography>
                <Typography variant='caption' fontWeight={600}>
                  {fmtHour(status.predicted_peak_hour)}
                </Typography>
              </Stack>
            </Stack>
          ) : (
            <Typography variant='caption' color='text.secondary'>
              Model active noon–8 PM ET on weekdays.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Today's demand + LMP chart */}
      <Card variant='outlined' sx={{ flex: 2, minWidth: 280 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack
            direction='row'
            alignItems='baseline'
            justifyContent='space-between'
            sx={{ mb: 1 }}
          >
            <Typography variant='subtitle2' fontWeight={700}>
              Today's Load & LMP
            </Typography>
            <Stack direction='row' spacing={1.5}>
              <Stack direction='row' spacing={0.5} alignItems='center'>
                <Box
                  sx={{
                    width: 12,
                    height: 2,
                    bgcolor: '#1976d2',
                    borderRadius: 1,
                  }}
                />
                <Typography variant='caption' color='text.secondary'>
                  Demand
                </Typography>
              </Stack>
              <Stack direction='row' spacing={0.5} alignItems='center'>
                <Box
                  sx={{
                    width: 12,
                    height: 2,
                    bgcolor: '#ed6c02',
                    borderRadius: 1,
                  }}
                />
                <Typography variant='caption' color='text.secondary'>
                  RTM
                </Typography>
              </Stack>
              <Stack direction='row' spacing={0.5} alignItems='center'>
                <Box
                  sx={{
                    width: 12,
                    height: 2,
                    bgcolor: '#ed6c02',
                    borderRadius: 1,
                    opacity: 0.5,
                  }}
                />
                <Typography variant='caption' color='text.secondary'>
                  DAM
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          {intervals.length > 0 ? (
            <TodayChart intervals={intervals} />
          ) : (
            <Box
              sx={{
                height: 140,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant='caption' color='text.secondary'>
                No interval data yet today.
              </Typography>
            </Box>
          )}

          {/* Annual peaks summary */}
          {annualPeaks.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography
                variant='caption'
                color='text.secondary'
                fontWeight={600}
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: 10,
                }}
              >
                Recent Coincident Peaks
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                {annualPeaks
                  .slice(-5)
                  .reverse()
                  .map((p) => (
                    <Stack
                      key={p.peak_date}
                      direction='row'
                      justifyContent='space-between'
                    >
                      <Typography variant='caption' color='text.secondary'>
                        {p.peak_date}
                      </Typography>
                      <Typography variant='caption' fontWeight={600}>
                        {fmtMw(p.peak_mw)}
                      </Typography>
                    </Stack>
                  ))}
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}

// ─── Markets ──────────────────────────────────────────────────────────────────

const GRID_DATA = 'grid-data';
const WEATHER = 'weather';
const COINCIDENCE_PEAKS = 'coincidence-peaks';
const TABS = [GRID_DATA, WEATHER, COINCIDENCE_PEAKS];

export default function Markets() {
  const { rtos } = useSelector((state) => state.demand);
  const [selectedTab, setSelectedTab] = useState(GRID_DATA);

  return (
    <Box sx={{ maxWidth: 1100, width: '100%', px: 1, mt: 1, mb: 5 }}>
      {/* ── Grid intelligence per RTO ──────────────────────────────────── */}
      <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1.5 }}>
        Grid Intelligence
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {map(rtos, (rto) => (
          <GridIntelligence key={rto.rto_id} rto={rto} />
        ))}
        {rtos.length === 0 && (
          <Typography variant='body2' color='text.secondary'>
            No grids configured.
          </Typography>
        )}
      </Stack>

      {/* ── Data sub-tabs ─────────────────────────────────────────────── */}
      <StyledSubtabs
        value={selectedTab}
        onChange={(_, v) => setSelectedTab(v)}
        indicatorColor='primary'
        textColor='primary'
        sx={{ mb: 1 }}
      >
        {TABS.map((t) => (
          <StyledSubtab key={t} label={startCase(t)} value={t} />
        ))}
      </StyledSubtabs>

      {selectedTab === GRID_DATA && <ModelingChartContainer />}
      {selectedTab === WEATHER && <WeatherChartContainer />}
      {selectedTab === COINCIDENCE_PEAKS && <CoincidencePeaks />}
    </Box>
  );
}
